import React from 'react';
import { FileText, Save, Sparkles, Mic, Music, BookOpen, Printer, Copy as CopyIcon } from 'lucide-react';

const MIN_CHARS = 50;

const wordCount = (text) => (text || '').trim().split(/\s+/).filter(Boolean).length;

const CAPABILITIES = [
  { Icon: BookOpen, title: 'Verse detection', text: 'Catches verses the preacher quotes without naming them (Bible > Voice).' },
  { Icon: Music, title: 'Song finder', text: 'Say the title or sing a few lines (Songs > Listen).' },
  { Icon: FileText, title: 'Sermon notes', text: 'Summary, key points and questions, below.' },
];

/** AI Tools view: SleekHear AI status and the sermon notes workflow (listen, summarize, share). */
const AiToolsView = ({
  activeView,
  copySummary,
  displayPassageByReference,
  dm,
  downloadSummary,
  groqEnabled,
  isSummarizing,
  printHandout,
  sermonSummary,
  sermonTitle,
  setSermonTitle,
  setSpeaker,
  speaker,
  summarizeSermon,
  t,
  transcribedText,
}) => {
  const words = wordCount(transcribedText);
  const ready = !!transcribedText && transcribedText.trim().length >= MIN_CHARS;
  const card = `rounded-2xl p-5 sm:p-6 border shadow-lg ${dm ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-gray-200'}`;
  const sectionTitle = `text-sm font-bold mb-2 ${t.textPrimary}`;
  const ghostBtn = 'px-3 py-2 bg-gradient-to-r from-slate-600 to-slate-700 text-slate-100 rounded-lg text-sm font-semibold hover:from-slate-500 hover:to-slate-600 transition-all flex items-center gap-1.5';

  return (
    <div className={`${activeView === 'ai' ? '' : 'hidden'} lg:col-span-2 space-y-4`}>

      {/* SleekHear AI status */}
      <div className={`rounded-2xl p-5 sm:p-6 border shadow-lg ${groqEnabled ? (dm ? 'bg-gradient-to-br from-emerald-900/40 to-teal-900/40 border-emerald-500/30' : 'bg-emerald-50 border-emerald-300') : (dm ? 'bg-slate-800/60 border-slate-700' : 'bg-gray-50 border-gray-200')}`}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <span className={`p-1.5 rounded-lg ${groqEnabled ? 'bg-gradient-to-br from-emerald-500 to-teal-500' : 'bg-gray-600'}`}>
              <Sparkles className="w-4 h-4 text-slate-50" />
            </span>
            <span className={groqEnabled ? (dm ? 'text-emerald-300' : 'text-emerald-700') : t.textSecondary}>SleekHear AI</span>
          </h2>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${groqEnabled ? (dm ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border border-emerald-300') : (dm ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-300')}`}>
            {groqEnabled ? 'Connected' : 'No API Key'}
          </span>
        </div>

        {groqEnabled ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {CAPABILITIES.map(({ Icon, title, text }) => (
              <div key={title} className={`rounded-xl p-3 ${dm ? 'bg-slate-900/40' : 'bg-white/70'}`}>
                <p className={`text-sm font-semibold flex items-center gap-1.5 mb-1 ${t.textPrimary}`}>
                  <Icon className="w-4 h-4 text-emerald-400" /> {title}
                </p>
                <p className={`text-xs leading-relaxed ${t.textSecondary}`}>{text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className={`text-sm ${t.textSecondary}`}>
            AI features are switched off because no API key is set. Everything else still works: verses are found by reference, and songs by title.
            Ask your administrator to add the key.
          </p>
        )}
      </div>

      {/* Sermon notes */}
      {groqEnabled && (
        <div className={card}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h2 className={`text-lg font-bold flex items-center gap-2 ${dm ? 'text-blue-300' : 'text-blue-700'}`}>
                <FileText className="w-5 h-5" /> Sermon notes
              </h2>
              <p className={`text-sm mt-0.5 ${t.textSecondary}`}>
                Listen during the sermon (Bible &gt; Voice &gt; Start), then summarize it here.
              </p>
            </div>
            <button
              onClick={summarizeSermon}
              disabled={isSummarizing || !ready}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-700 to-blue-600 text-slate-50 rounded-lg text-sm font-bold hover:from-blue-600 hover:to-blue-500 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSummarizing ? 'Generating...' : 'Summarize'}
            </button>
          </div>

          {/* What the AI will read */}
          <div className={`rounded-xl px-4 py-3 mb-4 flex items-center gap-3 text-sm ${dm ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
            <Mic className={`w-4 h-4 shrink-0 ${ready ? 'text-emerald-400' : t.textMuted}`} />
            <span className={t.textSecondary}>
              {ready
                ? <>Ready: <strong className={t.textPrimary}>{words.toLocaleString()} words</strong> heard so far.</>
                : 'Nothing to summarize yet. Start listening and let the sermon run for a minute or two.'}
            </span>
          </div>

          {isSummarizing && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-amber-400 mx-auto mb-3"></div>
              <p className={`text-sm ${t.textSecondary}`}>Analyzing sermon with AI...</p>
            </div>
          )}

          {!isSummarizing && sermonSummary && (
            <div className="space-y-5">
              {/* Handout details and sharing */}
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={sermonTitle}
                  onChange={(e) => setSermonTitle(e.target.value)}
                  placeholder="Sermon title (optional)"
                  aria-label="Sermon title"
                  className={`flex-1 min-w-[10rem] p-2.5 text-sm rounded-lg border focus:border-brand-500 outline-none ${t.inputBg}`}
                />
                <input
                  type="text"
                  value={speaker}
                  onChange={(e) => setSpeaker(e.target.value)}
                  placeholder="Speaker (optional)"
                  aria-label="Speaker"
                  className={`flex-1 min-w-[10rem] p-2.5 text-sm rounded-lg border focus:border-brand-500 outline-none ${t.inputBg}`}
                />
                <button onClick={printHandout} title="Open a printable handout (choose Save as PDF in the print window)" className={ghostBtn}>
                  <Printer className="w-4 h-4" /> Handout
                </button>
                <button onClick={copySummary} title="Copy the summary as text" className={ghostBtn}>
                  <CopyIcon className="w-4 h-4" /> Copy
                </button>
                <button onClick={downloadSummary} title="Download summary as text file" className={ghostBtn}>
                  <Save className="w-4 h-4" /> Download
                </button>
              </div>

              {sermonSummary.theme && (
                <div className={`p-3 rounded-xl border ${dm ? 'bg-amber-900/30 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                  <span className={`text-sm font-bold ${dm ? 'text-amber-300' : 'text-amber-600'}`}>Theme: </span>
                  <span className={`text-sm ${t.textPrimary}`}>{sermonSummary.theme}</span>
                </div>
              )}

              {sermonSummary.summary && (
                <div>
                  <h4 className={sectionTitle}>Summary</h4>
                  <p className={`text-sm leading-relaxed ${t.textSecondary}`}>{sermonSummary.summary}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {sermonSummary.keyPoints && sermonSummary.keyPoints.length > 0 && (
                  <div>
                    <h4 className={sectionTitle}>Key Points</h4>
                    <ul className="space-y-1.5">
                      {sermonSummary.keyPoints.map((point, idx) => (
                        <li key={idx} className={`text-sm flex items-start gap-2 ${t.textSecondary}`}>
                          <span className={`mt-0.5 ${dm ? 'text-amber-400' : 'text-amber-500'}`}>•</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {sermonSummary.discussionQuestions && sermonSummary.discussionQuestions.length > 0 && (
                  <div>
                    <h4 className={sectionTitle}>Discussion Questions</h4>
                    <ol className="space-y-1.5 list-decimal pl-5">
                      {sermonSummary.discussionQuestions.map((q, idx) => (
                        <li key={idx} className={`text-sm ${t.textSecondary}`}>{q}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>

              {sermonSummary.takeaway && (
                <div className={`p-3 rounded-xl border ${dm ? 'bg-emerald-900/30 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'}`}>
                  <span className={`text-sm font-bold ${dm ? 'text-emerald-300' : 'text-emerald-700'}`}>This week: </span>
                  <span className={`text-sm ${t.textPrimary}`}>{sermonSummary.takeaway}</span>
                </div>
              )}

              {sermonSummary.verses && sermonSummary.verses.length > 0 && (
                <div>
                  <h4 className={sectionTitle}>Referenced Verses</h4>
                  <p className={`text-xs mb-2 ${t.textMuted}`}>Click a verse to stage it.</p>
                  <div className="flex flex-wrap gap-2">
                    {sermonSummary.verses.map((verse, idx) => (
                      <button
                        key={idx}
                        onClick={() => displayPassageByReference(verse)}
                        className={`px-3 py-1 rounded-lg text-sm border transition-all ${dm ? 'bg-amber-900/50 text-amber-200 hover:bg-amber-800/50 border-amber-500/20' : 'bg-amber-100 text-amber-700 hover:bg-amber-200 border-amber-300'}`}
                      >
                        {verse}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AiToolsView;
